'use client';

import { useEffect, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { identifyUser, resetIdentity } from '@/lib/analytics/posthog';
import { dedupedActiveTenantFetch } from '@/hooks/useActiveTenant';

/**
 * Fetch tenantId + role from the canonical resolver instead of
 * reading client storage. Shares the in-flight promise with
 * ActiveTenantProvider so this costs zero extra network. Returns
 * `null` fields on failure (Sentry context is best-effort).
 */
async function resolveTenantContext(): Promise<{
  tenantId: string | null;
  role: string | null;
  tenantName: string | null;
}> {
  try {
    const res = await dedupedActiveTenantFetch(null);
    if (!res.ok) return { tenantId: null, role: null, tenantName: null };
    const data = (await res.json()) as {
      tenantId?: string | null;
      role?: string | null;
      name?: string | null;
    };
    return {
      tenantId: data.tenantId ?? null,
      role: data.role ?? null,
      tenantName: data.name ?? null,
    };
  } catch {
    return { tenantId: null, role: null, tenantName: null };
  }
}

async function applyUserContext(user: User): Promise<void> {
  // Tenant + role come from the canonical resolver (single source of truth).
  // The resolver also returns the tenant `name` so we save one DB query
  // compared to the legacy path.
  const ctx = await resolveTenantContext();
  const tenantId = ctx.tenantId ?? undefined;
  const role = ctx.role ?? undefined;
  const tenantName = ctx.tenantName ?? undefined;
  let subscriptionPlan: string | undefined;
  let subscriptionStatus: string | undefined;

  if (tenantId) {
    try {
      const { data: subscriptionData } = await supabase
        .from('tenant_subscriptions')
        .select('plan_id, status')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .maybeSingle();

      subscriptionPlan = subscriptionData?.plan_id;
      subscriptionStatus = subscriptionData?.status;
    } catch (error) {
      // Silent fail - Sentry context is optional
      console.warn('Failed to fetch Sentry context data:', error);
    }
  }

  setSentryUser({
    id: user.id,
    email: user.email,
    username: user.user_metadata?.full_name || user.email?.split('@')[0],
    tenantId,
    tenantName,
    role,
    subscriptionPlan,
    subscriptionStatus,
  });

  identifyUser(user.id, {
    email: user.email,
    tenantId,
    plan: subscriptionPlan,
    trial: subscriptionStatus === 'trialing',
  });
}

function deferUserContextUpdate(user: User): void {
  setTimeout(() => {
    void applyUserContext(user);
  }, 0);
}

interface SentryUserProviderProps {
  children: ReactNode;
}

/**
 * 🔐 Sentry User Context Provider
 *
 * Automatically sets Sentry user context when user logs in
 * and clears it when they log out.
 *
 * Place this high in the component tree (e.g., in layout)
 */
export function SentryUserProvider({ children }: SentryUserProviderProps) {
  useEffect(() => {
    // Set initial user context
    const setUserContext = async () => {
      const { data } = await supabase.auth.getSession();
      const user = data.session?.user;

      if (user) {
        deferUserContextUpdate(user);
      }
    };

    void setUserContext();

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // User signed in - set context via canonical resolver
          // (which also issues the HttpOnly tenant cookie as a side-
          // effect, so the rest of the app has fresh context too).
          deferUserContextUpdate(session.user);
        } else if (event === 'SIGNED_OUT') {
          // User signed out - clear context
          clearSentryUser();
          resetIdentity();
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return <>{children}</>;
}
